import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { toast } from 'sonner';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { QUERY_KEYS } from '~shared/api/query-keys';

import type { PushScenarioToggle } from '../model/types';

type PushScenariosQueryData = { scenarios: Array<PushScenarioToggle> };

const UPDATE_SCENARIO_MUTATION_KEY = [
  'mobile-inventor-push',
  'update-scenario',
] as const;

export const updatePushScenarioAPI = async (params: {
  name: string;
  enabled: boolean;
}) => {
  const response = await baseAPI.patch(
    `mobile-inventor-push/scenarios/${params.name}`,
    { enabled: params.enabled },
  );
  return response.data;
};

export const useUpdatePushScenario = () =>
  useMutation({
    mutationKey: UPDATE_SCENARIO_MUTATION_KEY,
    mutationFn: updatePushScenarioAPI,
    onMutate: async (params) => {
      const queryKey = QUERY_KEYS.MOBILE_INVENTOR_PUSH_SCENARIOS();
      await queryClient.cancelQueries({ queryKey });

      const previous =
        queryClient.getQueryData<PushScenariosQueryData>(queryKey);

      queryClient.setQueryData<PushScenariosQueryData>(queryKey, (current) => {
        if (!current?.scenarios) return current;
        return {
          ...current,
          scenarios: current.scenarios.map((scenario) =>
            scenario.name === params.name
              ? { ...scenario, enabled: params.enabled }
              : scenario,
          ),
        };
      });

      return { previous };
    },
    onError: (error: AxiosError<{ message?: string }>, params, context) => {
      // Revert only this scenario. Restoring the full onMutate snapshot would
      // wipe concurrent optimistic (or already persisted) toggles from other cards.
      const previousEnabled = context?.previous?.scenarios?.find(
        (scenario) => scenario.name === params.name,
      )?.enabled;
      if (previousEnabled !== undefined) {
        queryClient.setQueryData<PushScenariosQueryData>(
          QUERY_KEYS.MOBILE_INVENTOR_PUSH_SCENARIOS(),
          (current) => {
            if (!current?.scenarios) return current;
            return {
              ...current,
              scenarios: current.scenarios.map((scenario) =>
                scenario.name === params.name
                  ? { ...scenario, enabled: previousEnabled }
                  : scenario,
              ),
            };
          },
        );
      }
      toast.error(error.response?.data?.message ?? 'Failed to update scenario');
    },
    onSettled: () => {
      // Wait until every overlapping toggle finishes before refetching so the
      // first mutation's invalidate cannot overwrite another in-flight
      // optimistic value with stale server data.
      // isMutating still includes this settling mutation, so only skip when
      // another toggle is also in flight (> 1).
      if (
        queryClient.isMutating({ mutationKey: UPDATE_SCENARIO_MUTATION_KEY }) >
        1
      ) {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.MOBILE_INVENTOR_PUSH_SCENARIOS(),
      });
    },
  });
